/**
 * Catálogo de Tipos de Documentos
 * Fecha: 16 Oct 2025
 * 
 * Vista CRUD completa para administración del catálogo de tipos de documentos
 * con configuración de requisitos y vencimientos
 */

import React, { useState, useEffect } from 'react';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';
import SelectBuscable from '@/shared/components/SelectBuscable'
import { Checkbox } from '@/shared/components/Checkbox'
import { documentTypesService } from '@/features/configuracion/api/documentTypes.service';
import type { DocumentType, DocumentCategory } from '@/types/employee.types';
import {
  LoadingSpinner,
  Badge,
  Modal,
  ConfirmModal,
  PermissionGuard,
} from '@/shared/components';
import {
  Plus,
  Pen,
  Trash2,
  CheckCircle,
  XCircle,
  FileText,
  AlertCircle,
  Calendar,
} from 'lucide-react';
import { EMPLOYEE_PERMISSIONS } from '@/shared/hooks/usePermissions';
import toast from 'react-hot-toast';
import KpiCard from '@/features/rh/components/dashboard/KpiCard';

const CATEGORIAS: { value: DocumentCategory; label: string; description: string }[] = [
  { 
    value: 'obligatorio_general', 
    label: 'Obligatorio General',
    description: 'Documentos requeridos para todos los empleados'
  },
  { 
    value: 'obligatorio_rh', 
    label: 'Obligatorio RH',
    description: 'Documentos requeridos por el departamento de Recursos Humanos'
  },
  { 
    value: 'condicional_rh', 
    label: 'Condicional RH',
    description: 'Documentos que pueden ser requeridos según el caso'
  },
  { 
    value: 'ehs_cliente', 
    label: 'EHS Cliente',
    description: 'Documentos de seguridad e higiene requeridos por el cliente'
  },
];

export const DocumentTypesCatalog: React.FC = () => {
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoria, setFilterCategoria] = useState<string>('');
  const [filterObligatorio, setFilterObligatorio] = useState<string>('');

  // Create/Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDocType, setEditDocType] = useState<DocumentType | null>(null);
  const [formNombre, setFormNombre] = useState('');
  const [formDescripcion, setFormDescripcion] = useState('');
  const [formCategoria, setFormCategoria] = useState<DocumentCategory>('obligatorio_general');
  const [formEsObligatorio, setFormEsObligatorio] = useState(false);
  const [formTieneVencimiento, setFormTieneVencimiento] = useState(false);
  const [formDiasValidez, setFormDiasValidez] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteDocType, setDeleteDocType] = useState<DocumentType | null>(null);

  // Activate/Deactivate Modals
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [activateDocType, setActivateDocType] = useState<DocumentType | null>(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateDocType, setDeactivateDocType] = useState<DocumentType | null>(null);

  useEffect(() => {
    loadDocumentTypes();
  }, []);

  const loadDocumentTypes = async () => {
    try {
      setIsLoading(true);
      const data = await documentTypesService.getAll();
      setDocumentTypes(data);
    } catch (error) {
      console.error('Error al cargar tipos de documentos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateClick = () => {
    setEditDocType(null);
    setFormNombre('');
    setFormDescripcion('');
    setFormCategoria('obligatorio_general');
    setFormEsObligatorio(false);
    setFormTieneVencimiento(false);
    setFormDiasValidez(null);
    setShowEditModal(true);
  };

  const handleEditClick = (docType: DocumentType) => {
    setEditDocType(docType);
    setFormNombre(docType.nombre);
    setFormDescripcion(docType.descripcion || '');
    setFormCategoria(docType.categoria);
    setFormEsObligatorio(docType.esObligatorio);
    setFormTieneVencimiento(docType.tieneVencimiento);
    setFormDiasValidez(docType.diasValidez);
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!formNombre.trim()) {
      toast.error('Por favor ingresa el nombre del tipo de documento');
      return;
    }

    if (formTieneVencimiento && (!formDiasValidez || formDiasValidez <= 0)) {
      toast.error('Por favor ingresa los días de validez para documentos con vencimiento');
      return;
    }

    try {
      setIsSaving(true);

      if (editDocType) {
        // Update
        await documentTypesService.update(editDocType.id, {
          nombre: formNombre,
          descripcion: formDescripcion || undefined,
          categoria: formCategoria,
          esObligatorio: formEsObligatorio,
          tieneVencimiento: formTieneVencimiento,
          diasValidez: formTieneVencimiento && formDiasValidez ? formDiasValidez : undefined,
        });
      } else {
        // Create
        await documentTypesService.create({
          nombre: formNombre,
          descripcion: formDescripcion || undefined,
          categoria: formCategoria,
          esObligatorio: formEsObligatorio,
          tieneVencimiento: formTieneVencimiento,
          diasValidez: formTieneVencimiento && formDiasValidez ? formDiasValidez : undefined,
        });
      }

      await loadDocumentTypes();
      setShowEditModal(false);
    } catch (error) {
      console.error('Error al guardar tipo de documento:', error);
      toast.error('Error al guardar el tipo de documento');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (docType: DocumentType) => {
    setDeleteDocType(docType);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteDocType) return;

    try {
      await documentTypesService.deactivate(deleteDocType.id);
      await loadDocumentTypes();
      setShowDeleteModal(false);
      setDeleteDocType(null);
    } catch (error) {
      console.error('Error al eliminar tipo de documento:', error);
      toast.error('Error al eliminar el tipo de documento');
    }
  };

  const handleActivateClick = (docType: DocumentType) => {
    setActivateDocType(docType);
    setShowActivateModal(true);
  };

  const handleConfirmActivate = async () => {
    if (!activateDocType) return;

    try {
      await documentTypesService.activate(activateDocType.id);
      await loadDocumentTypes();
      setShowActivateModal(false);
      setActivateDocType(null);
    } catch (error) {
      console.error('Error al activar tipo de documento:', error);
      toast.error('Error al activar el tipo de documento');
    }
  };

  const handleDeactivateClick = (docType: DocumentType) => {
    setDeactivateDocType(docType);
    setShowDeactivateModal(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateDocType) return;

    try {
      await documentTypesService.deactivate(deactivateDocType.id);
      await loadDocumentTypes();
      setShowDeactivateModal(false);
      setDeactivateDocType(null);
    } catch (error) {
      console.error('Error al desactivar tipo de documento:', error);
      toast.error('Error al desactivar el tipo de documento');
    }
  };

  // Filters
  const filteredDocTypes = documentTypes.filter((docType) => {
    const matchesSearch = docType.nombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategoria = !filterCategoria || docType.categoria === filterCategoria;
    const matchesObligatorio = 
      !filterObligatorio || 
      (filterObligatorio === 'obligatorio' && docType.esObligatorio) ||
      (filterObligatorio === 'opcional' && !docType.esObligatorio);
    return matchesSearch && matchesCategoria && matchesObligatorio;
  });

  // Stats
  const totalDocTypes = documentTypes.length;
  const activeDocTypes = documentTypes.filter((d) => d.activo).length;
  const obligatorios = documentTypes.filter((d) => d.esObligatorio).length;
  const conVencimiento = documentTypes.filter((d) => d.tieneVencimiento).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Catálogo de Tipos de Documentos
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Administra los tipos de documentos utilizados en el sistema
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PermissionGuard permission={EMPLOYEE_PERMISSIONS.DOCUMENT_TYPES_CREATE}>
            <button
              onClick={handleCreateClick}
              className={estiloDeBoton('primario')}
            >
              <Plus className="w-4 h-4" />
              Crear Tipo de Documento
            </button>
          </PermissionGuard>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard title="Total" value={totalDocTypes} icon={<FileText />} variant="primary" />
        <KpiCard title="Activos" value={activeDocTypes} icon={<CheckCircle />} variant="success" />
        <KpiCard title="Obligatorios" value={obligatorios} icon={<AlertCircle />} variant="warning" />
        <KpiCard title="Con Vencimiento" value={conVencimiento} icon={<Calendar />} variant="purple" />
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Categoría
            </label>
            <SelectBuscable
              variante="listado"
              todos="Todas las categorías"
              value={filterCategoria}
              onChange={setFilterCategoria}
              opciones={CATEGORIAS.map((cat) => ({ value: cat.value, label: cat.label }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Requisito
            </label>
            <SelectBuscable
              variante="listado"
              todos="Todos"
              value={filterObligatorio}
              onChange={setFilterObligatorio}
              opciones={[
                { value: 'obligatorio', label: 'Obligatorios' },
                { value: 'opcional', label: 'Opcionales' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Document Types List */}
      {filteredDocTypes.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-12 border border-gray-200 dark:border-gray-700 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            No se encontraron tipos de documentos
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredDocTypes.map((docType) => (
            <div
              key={docType.id}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {docType.nombre}
                    </h3>
                    <Badge color={docType.activo ? 'verde' : 'gris'}>
                      {docType.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                    {docType.esObligatorio && (
                      <Badge color="amarillo">
                        Obligatorio
                      </Badge>
                    )}
                    {docType.tieneVencimiento && (
                      <Badge color="azul" icon={<Calendar className="w-3 h-3" />}>
                        Vence en {docType.diasValidez} días
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-2">
                    <Badge color="morado">
                      {CATEGORIAS.find((c) => c.value === docType.categoria)?.label || docType.categoria}
                    </Badge>
                    <span>ID: {docType.id}</span>
                  </div>

                  {docType.descripcion && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                      {docType.descripcion}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {docType.activo ? (
                    <PermissionGuard permission={EMPLOYEE_PERMISSIONS.DOCUMENT_TYPES_UPDATE}>
                      <button
                        onClick={() => handleDeactivateClick(docType)}
                        className="p-2 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-md"
                        title="Desactivar"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </PermissionGuard>
                  ) : (
                    <PermissionGuard permission={EMPLOYEE_PERMISSIONS.DOCUMENT_TYPES_UPDATE}>
                      <button
                        onClick={() => handleActivateClick(docType)}
                        className="p-2 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-md"
                        title="Activar"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    </PermissionGuard>
                  )}

                  <PermissionGuard permission={EMPLOYEE_PERMISSIONS.DOCUMENT_TYPES_UPDATE}>
                    <button
                      onClick={() => handleEditClick(docType)}
                      className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md"
                      title="Editar"
                    >
                      <Pen className="h-4 w-4" />
                    </button>
                  </PermissionGuard>

                  <PermissionGuard permission={EMPLOYEE_PERMISSIONS.DOCUMENT_TYPES_DELETE}>
                    <button
                      onClick={() => handleDeleteClick(docType)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </PermissionGuard>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={editDocType ? 'Editar Tipo de Documento' : 'Crear Tipo de Documento'}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formNombre}
              onChange={(e) => setFormNombre(e.target.value)}
              placeholder="Ej: Acta de Nacimiento"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Descripción
            </label>
            <textarea
              value={formDescripcion}
              onChange={(e) => setFormDescripcion(e.target.value)}
              rows={3}
              placeholder="Descripción detallada del tipo de documento..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Categoría <span className="text-red-500">*</span>
            </label>
            <SelectBuscable
              variante="formulario"
              value={formCategoria}
              onChange={(valor) => setFormCategoria(valor as DocumentCategory)}
              canDelete={false}
              opciones={CATEGORIAS.map((cat) => ({ value: cat.value, label: cat.label }))}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {CATEGORIAS.find((c) => c.value === formCategoria)?.description}
            </p>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Checkbox
              id="esObligatorio"
              checked={formEsObligatorio}
              onChange={(e) => setFormEsObligatorio(e.target.checked)}
            />
            <label htmlFor="esObligatorio" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
              Este documento es obligatorio
            </label>
          </div>

          <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-3">
              <Checkbox
                id="tieneVencimiento"
                checked={formTieneVencimiento}
                onChange={(e) => setFormTieneVencimiento(e.target.checked)}
              />
              <label htmlFor="tieneVencimiento" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                Este documento tiene fecha de vencimiento
              </label>
            </div>

            {formTieneVencimiento && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Días de Validez <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formDiasValidez || ''}
                  onChange={(e) => setFormDiasValidez(e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="365"
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Número de días desde la fecha de emisión hasta que el documento vence
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => setShowEditModal(false)}
              disabled={isSaving}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={estiloDeBoton('primario')}
            >
              {isSaving && <LoadingSpinner size="sm" />}
              {editDocType ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Activate Confirmation Modal */}
      <ConfirmModal
        isOpen={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        onConfirm={handleConfirmActivate}
        title="Activar Tipo de Documento"
        message={`¿Estás seguro de que deseas activar el tipo de documento "${activateDocType?.nombre}"?`}
        confirmText="Activar"
      />

      {/* Deactivate Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={handleConfirmDeactivate}
        title="Desactivar Tipo de Documento"
        message={`¿Estás seguro de que deseas desactivar el tipo de documento "${deactivateDocType?.nombre}"? No podrá usarse para nuevos documentos.`}
        confirmText="Desactivar"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Eliminar Tipo de Documento"
        message={`¿Estás seguro de que deseas eliminar el tipo de documento "${deleteDocType?.nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
      />
    </div>
  );
};
